import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Box,
  Button,
  Container,
  createTheme,
  ThemeProvider,
  useMediaQuery,
  Stack,
  Chip,
  Paper,
  Typography,
  LinearProgress,
  Alert
} from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import { Refresh, Today } from '@mui/icons-material';
import Cookies from 'js-cookie';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#581745' },       // Updated primary color
    secondary: { main: '#14b8a6' },
    background: { default: '#f8fafc' }
  },
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif'
    ].join(',')
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: '8px',
          padding: '8px 16px',
          boxShadow: 'none',
          color: '#fff',                      // Force text white
          '&.MuiButton-containedPrimary': {
            backgroundColor: '#581745',      // Primary bg
            color: '#fff',                   // Primary text
          },
          '&:hover': {
            backgroundColor: '#450f35',      // Slightly darker hover
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          border: '1px solid #e2e8f0'
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '6px'
        }
      }
    }
  }
});

export default function Scheduler() {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [startDate, setStartDate] = useState(dayjs().subtract(7, 'day'));
  const [endDate, setEndDate] = useState(dayjs());
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const token = Cookies.get("authToken");

  // dynamic height to fill available viewport space
  const tableHeight = isMobile ? '400px' : 'calc(100vh - 300px)';

  const columns = [
    { accessorKey: 'id', header: 'ID', size: 80 },
    {
      accessorKey: 'status',
      header: 'Status',
      Cell: ({ cell }) => {
        const val = cell.getValue();
        const statusConfig = {
          pending: { color: '#f59e0b', bg: '#fffbeb' },
          sent: { color: '#10b981', bg: '#ecfdf5' },
          failed: { color: '#ef4444', bg: '#fef2f2' }
        };
        return (
          <Chip
            label={val.toUpperCase()}
            size="small"
            sx={{
              bgcolor: statusConfig[val]?.bg || '#e2e8f0',
              color: statusConfig[val]?.color || '#64748b',
              fontWeight: 600,
              px: 1,
              borderRadius: '4px'
            }}
          />
        );
      }
    },
    {
      accessorKey: 'api_name',
      header: 'API Name',
      Cell: ({ cell }) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {cell.getValue()}
        </Typography>
      )
    },
    {
      accessorKey: 'scheduled_at',
      header: 'Scheduled',
      Cell: ({ cell }) => dayjs(cell.getValue()).format('DD MMM YY · HH:mm'),
      size: 150
    },
    {
      accessorKey: 'created_at',
      header: 'Created At',
      Cell: ({ cell }) => dayjs(cell.getValue()).format('DD MMM YY · HH:mm'),
      size: 150
    },
    {
      accessorKey: 'attempts',
      header: 'Attempts',
      Cell: ({ cell }) => (
        <Chip
          label={cell.getValue()}
          size="small"
          variant="outlined"
          sx={{ borderRadius: '4px' }}
        />
      )
    },
    {
      accessorKey: 'sent_at',
      header: 'Sent At',
      Cell: ({ cell }) =>
        cell.getValue()
          ? dayjs(cell.getValue()).format('DD MMM YY · HH:mm')
          : '-',
      size: 150
    }
  ];

  useEffect(() => {
    const token = Cookies.get("authToken");
    if (!token) window.location.href = "/";
  }, []);

  const handleFetch = async () => {
    setError(null);
    setLoading(true);
    try {
      const s = startDate.format('YYYY-MM-DD');
      const e = endDate.format('YYYY-MM-DD');
      const resp = await fetch(
        `/api/api/scheduled_logs?start=${s}&end=${e}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        });
      if (!resp.ok) throw new Error(`Request failed: ${resp.status}`);
      const result = await resp.json();
      setData(result);
    } catch (err) {
      setError(err.message);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickSelect = (days) => {
    setStartDate(dayjs().subtract(days, 'day'));
    setEndDate(dayjs());
  };

  const hiddenColumns = isMobile ? ['sent_at'] : [];

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Container
          maxWidth="xl"
          sx={{ py: 4, display: 'flex', flexDirection: 'column', height: '100vh' }}
        >
          <Stack spacing={3} sx={{ flexGrow: 1 }}>
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2
              }}
            >
              <Typography variant="h5" fontWeight={700}>
                Scheduler Logs
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={handleFetch}
                disabled={loading}
                startIcon={<Refresh />}
              >
                {loading ? 'Refreshing...' : 'Fetch Data'}
              </Button>
            </Box>

            {/* Filters */}
            <Paper sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Stack
                  direction={isMobile ? 'column' : 'row'}
                  spacing={2}
                  alignItems={isMobile ? 'stretch' : 'center'}
                >
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={setStartDate}
                    slots={{ openPickerIcon: Today }}
                    slotProps={{ textField: { size: 'small', fullWidth: isMobile } }}
                  />
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={setEndDate}
                    slots={{ openPickerIcon: Today }}
                    slotProps={{ textField: { size: 'small', fullWidth: isMobile } }}
                  />
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ ml: isMobile ? 0 : 'auto', flexWrap: 'wrap', gap: 1 }}
                  >
                    {[7, 30, 60].map((d) => (
                      <Chip
                        key={d}
                        label={`Last ${d} days`}
                        onClick={() => handleQuickSelect(d)}
                        variant={dayjs().diff(startDate, 'day') === d ? 'filled' : 'outlined'}
                        color="secondary"
                        clickable
                      />
                    ))}
                  </Stack>
                </Stack>
              </Stack>
            </Paper>

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                Error fetching data: {error}
              </Alert>
            )}

            {/* Data Table */}
            <Paper sx={{ position: 'relative', overflow: 'hidden' }}>
              {loading && (
                <LinearProgress
                  sx={{ position: 'absolute', top: 0, left: 0, right: 0 }}
                />
              )}
              <MaterialReactTable
                columns={columns}
                data={data}
                enableColumnActions={false}
                enableSorting
                enablePagination
                enableDensityToggle={false}
                enableFullScreenToggle={false}
                initialState={{
                  density: 'compact',
                  hiddenColumns,
                  pagination: { pageSize: isMobile ? 5 : 10 }
                }}
                muiTablePaperProps={{ elevation: 0 }}
                muiTableContainerProps={{ sx: { maxHeight: tableHeight } }}
                muiTableHeadProps={{
                  sx: {
                    '& th': {
                      fontWeight: 600,
                      bgcolor: '#f8fafc'
                    }
                  }
                }}
                muiTableBodyProps={{
                  sx: {
                    '& tr:hover td': { bgcolor: '#f8fafc' }
                  }
                }}
                renderTopToolbarCustomActions={() => (
                  <Typography variant="body2" color="text.secondary">
                    Showing {data.length} records
                  </Typography>
                )}
              />
            </Paper>
          </Stack>
        </Container>
      </LocalizationProvider>
    </ThemeProvider>
  );
}
